import React, { useEffect, useCallback, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { login, clearError } from '../store/slices/authSlice';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';

interface LoginFormData {
  email: string;
  password: string;
}

interface ForgotPasswordFormData {
  email: string;
}

const loginSchema = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required')
    .min(3, 'Email must be at least 3 characters')
    .max(50, 'Email must not exceed 50 characters'),
  password: yup
    .string()
    .required('Password is required')
    .min(3, 'Password must be at least 3 characters')
    .max(20, 'Password must not exceed 20 characters'),
});

const forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
});

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated, user } = useAppSelector((state: any) => state.auth);
  
  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  const {
    control: forgotPasswordControl,
    handleSubmit: handleForgotPasswordSubmit,
    formState: { errors: forgotPasswordErrors },
    reset: resetForgotPasswordForm,
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleNavigation = useCallback(() => {
    const returnUrl = location.state?.from?.pathname || '/dashboard';
    navigate(returnUrl);
  }, [navigate, location.state]);

  useEffect(() => {
    // Clear any existing errors when component mounts
    dispatch(clearError());
    
    // Redirect if already authenticated
    if (isAuthenticated && user) {
      handleNavigation();
    }
  }, [dispatch, isAuthenticated, user, handleNavigation]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      await dispatch(login({ email: data.email, password: data.password })).unwrap();
      toast.success('Login successful!');
      
      // Navigate to dashboard or return URL
      handleNavigation();
    } catch (error: any) {
      toast.error(error.message || 'Login failed');
    }
  };

  const handleForgotPassword = async (data: ForgotPasswordFormData) => {
    try {
      setForgotPasswordLoading(true);
      const response = await authAPI.forgotPassword(data.email);
      toast.success(response.data.message || 'Password reset link sent to your email');
      setForgotPasswordOpen(false);
      resetForgotPasswordForm();
    } catch (error: any) {
      const apiError = error.response?.data;
      let errorMessage = 'Failed to send reset link';
      if (apiError?.detail?.responseData?.message) {
        errorMessage = apiError.detail.responseData.message;
      } else if (apiError?.detail?.responseMessage) {
        errorMessage = apiError.detail.responseMessage;
      } else if (apiError?.message) {
        errorMessage = apiError.message;
      }
      toast.error(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleOpenForgotPassword = () => {
    setForgotPasswordOpen(true);
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    resetForgotPasswordForm();
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Welcome to Rezlive
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Sign in to your account
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    margin="normal"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    margin="normal"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={loading}
                    sx={{ mb: 3 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={handleTogglePassword}
                            edge="end"
                            tabIndex={-1}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
                <Button
                  variant="text"
                  color="primary"
                  disabled={loading}
                  onClick={handleOpenForgotPassword}
                  sx={{ textTransform: 'none' }}
                >
                  Forgot Password?
                </Button>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ 
                  py: 1.5, 
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Paper>

      {/* Forgot Password Modal */}
      <Dialog open={forgotPasswordOpen} onClose={handleCloseForgotPassword} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Forgot Password
          </Typography>
        </DialogTitle>
        <form onSubmit={handleForgotPasswordSubmit(handleForgotPassword)}>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
            
            <Controller
              name="email"
              control={forgotPasswordControl}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  margin="normal"
                  error={!!forgotPasswordErrors.email}
                  helperText={forgotPasswordErrors.email?.message}
                  disabled={forgotPasswordLoading}
                />
              )}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={handleCloseForgotPassword}
              disabled={forgotPasswordLoading}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={forgotPasswordLoading}
              sx={{ 
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                }
              }}
            >
              {forgotPasswordLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default Login; 